/**
 * Gabarits de page : la structure, les espacements et le comportement
 * responsive vivent ici ; un écran ne fournit que ses données et ses blocs.
 *
 * - HubPage : page d'orientation (Mon entreprise, Matériel, Menu).
 * - ListPage : en-tête, outils de liste, liste paginée ; au téléphone, phrase d'usage
 *   sur une ligne et action principale collée au pouce.
 * - DetailPage : fiche (onglets, contenu, colonne de contexte, barre d'action).
 * - CreatePage : saisie à étapes (copie de secours, pied d'envoi).
 * - SettingsPage : réglages en sections, largeur de lecture.
 * Règles de placement : docs/agents/UI.md.
 */
import type { ReactNode } from 'react';
import { DraftNotice, type useDraftBackup } from '../../core/draft-backup.js';
import { PageHeader } from '../ui/PageHeader.js';
import { ActionBar } from '../ui/ActionBar.js';
import { ActionButton } from '../ui/action.js';
import { DetailLayout } from '../ui/DetailLayout.js';

export type PageHeaderProps = Parameters<typeof PageHeader>[0];
type ActionBarProps = Parameters<typeof ActionBar>[0];

export function HubPage(props: { header: PageHeaderProps; children: ReactNode }): ReactNode {
  return (
    <div className="ui-page ui-page--hub">
      <PageHeader {...props.header} />
      {props.children}
    </div>
  );
}

export function ListPage(props: {
  header: PageHeaderProps;
  /** ListToolbar : recherche et filtres. */
  toolbar?: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="ui-page ui-page--list">
      <PageHeader {...props.header} compactDescription primaryAtThumb={Boolean(props.header.primary)} />
      {props.toolbar}
      {props.children}
      {/* Téléphone : l'action principale de la liste est à portée du pouce, plus dans l'en-tête. */}
      {props.header.primary ? (
        <div className="ui-list-thumb" data-ui="list-thumb">
          <ActionButton action={props.header.primary} large />
        </div>
      ) : null}
    </div>
  );
}

export function DetailPage(props: {
  header: PageHeaderProps;
  /** Prochaine étape de la fiche, avant le contenu. */
  next?: ReactNode;
  /** Colonne de contexte sur grand écran (client, site, montants…). */
  aside?: ReactNode;
  /** L'action principale de l'état courant, collée au pouce. */
  actionBar?: ActionBarProps;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="ui-page ui-page--detail">
      <PageHeader {...props.header} />
      {props.next}
      <DetailLayout main={props.children} aside={props.aside} />
      {props.actionBar ? <ActionBar {...props.actionBar} /> : null}
    </div>
  );
}

export function CreatePage(props: {
  header: PageHeaderProps;
  draft?: ReturnType<typeof useDraftBackup>;
  /** FormFooter : ce qui manque, puis le bouton d'enregistrement. */
  footer: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="ui-page form-page">
      <PageHeader {...props.header} />
      {props.draft ? <DraftNotice draft={props.draft} /> : null}
      {props.children}
      {props.footer}
    </div>
  );
}

export function SettingsPage(props: { header: PageHeaderProps; children: ReactNode }): ReactNode {
  return (
    <div className="ui-page ui-page--settings">
      <PageHeader {...props.header} />
      {props.children}
    </div>
  );
}
